import React from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Avatar,
  Chip
} from '@mui/material';
import { CheckCircle, Pets, Add, Visibility, ArrowBack } from '@mui/icons-material';
import { PetProfile } from '../../types/onboarding';

interface CompletionStepProps {
  petData: Partial<PetProfile>;
  onAddAnotherPet?: () => void;
  onCancel?: () => void;
  userLanguage: 'en' | 'zh-CN' | 'es' | 'fr';
}

export const CompletionStep: React.FC<CompletionStepProps> = ({
  petData,
  onAddAnotherPet,
  onCancel,
  userLanguage
}) => {
  // const { t } = useTranslation(); // Not used in this component

  // Language-specific content
  const getContent = () => {
    const content = {
      en: {
        title: "Welcome to the Family! 🎉",
        congratulations: "Congratulations! Your pet's profile has been created successfully.",
        petName: "Meet",
        profileComplete: "Profile Complete",
        viewProfile: "View Profile",
        addAnotherPet: "Add Another Pet",
        petDetails: "Pet Details",
        breed: "Breed",
        age: "Age",
        sex: "Sex",
        type: "Type",
        nextSteps: "What's Next?",
        nextStepsDesc: "You can now access personalized care recommendations, training plans, and track your pet's progress.",
        exploreFeatures: "Explore Features",
        startExploring: "Start Exploring PetSnap!",
        backToPetSnap: "Back to PetSnap"
      },
      'zh-CN': {
        title: "欢迎加入家庭！🎉",
        congratulations: "恭喜！您的宠物资料已成功创建。",
        petName: "认识",
        profileComplete: "资料完成",
        viewProfile: "查看资料",
        addAnotherPet: "添加另一只宠物",
        petDetails: "宠物详细信息",
        breed: "品种",
        age: "年龄",
        sex: "性别",
        type: "类型",
        nextSteps: "下一步是什么？",
        nextStepsDesc: "您现在可以访问个性化的护理建议、训练计划，并跟踪宠物的进度。",
        exploreFeatures: "探索功能",
        startExploring: "开始探索PetSnap！",
        backToPetSnap: "返回PetSnap"
      },
      es: {
        title: "¡Bienvenido a la Familia! 🎉",
        congratulations: "¡Felicitaciones! El perfil de tu mascota ha sido creado exitosamente.",
        petName: "Conoce a",
        profileComplete: "Perfil Completado",
        viewProfile: "Ver Perfil",
        addAnotherPet: "Agregar Otra Mascota",
        petDetails: "Detalles de la Mascota",
        breed: "Raza",
        age: "Edad",
        sex: "Sexo",
        type: "Tipo",
        nextSteps: "¿Qué Sigue?",
        nextStepsDesc: "Ahora puedes acceder a recomendaciones de cuidado personalizadas, planes de entrenamiento y rastrear el progreso de tu mascota.",
        exploreFeatures: "Explorar Características",
        startExploring: "¡Comienza a Explorar PetSnap!",
        backToPetSnap: "Volver a PetSnap"
      },
      fr: {
        title: "Bienvenue dans la Famille ! 🎉",
        congratulations: "Félicitations ! Le profil de votre animal a été créé avec succès.",
        petName: "Rencontrez",
        profileComplete: "Profil Terminé",
        viewProfile: "Voir le Profil",
        addAnotherPet: "Ajouter un Autre Animal",
        petDetails: "Détails de l'Animal",
        breed: "Race",
        age: "Âge",
        sex: "Sexe",
        type: "Type",
        nextSteps: "Quelle est la Prochaine Étape ?",
        nextStepsDesc: "Vous pouvez maintenant accéder à des recommandations de soins personnalisées, des plans d'entraînement et suivre les progrès de votre animal.",
        exploreFeatures: "Explorer les Fonctionnalités",
        startExploring: "Commencez à Explorer PetSnap !",
        backToPetSnap: "Retour à PetSnap"
      }
    };

    return content[userLanguage] || content.en;
  };

  const content = getContent();

  const formatGender = (gender?: string) => {
    if (!gender) return 'Unknown';

    const genderMap = {
      en: { male: 'Male', female: 'Female', unknown: 'Unknown' },
      'zh-CN': { male: '雄性', female: '雌性', unknown: '未知' },
      es: { male: 'Macho', female: 'Hembra', unknown: 'Desconocido' },
      fr: { male: 'Mâle', female: 'Femelle', unknown: 'Inconnu' }
    };

    return genderMap[userLanguage]?.[gender as keyof typeof genderMap.en] || gender;
  };

  const formatType = (type?: string) => {
    if (!type) return 'Unknown';

    const typeMap = {
      en: { dog: 'Dog', cat: 'Cat' },
      'zh-CN': { dog: '狗', cat: '猫' },
      es: { dog: 'Perro', cat: 'Gato' },
      fr: { dog: 'Chien', cat: 'Chat' }
    };

    return typeMap[userLanguage]?.[type as keyof typeof typeMap.en] || type;
  };

  return (
    <Box textAlign="center">
      {/* Success Header */}
      <Box mb={4}>
        <CheckCircle sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
        <Typography variant="h3" component="h1" gutterBottom color="success.main">
          {content.title}
        </Typography>
        <Typography variant="h5" component="h2" gutterBottom>
          {content.petName} <strong>{petData.name}</strong>!
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {content.congratulations}
        </Typography>
      </Box>

      {/* Pet Photo and Summary */}
      <Box sx={{ maxWidth: 800, mx: 'auto', mb: 4, display: 'flex', flexWrap: 'wrap', gap: 4, justifyContent: 'center' }}>
        {/* Pet Photo */}
        {petData.profilePhoto && (
          <Box sx={{ flex: '1 1 350px', maxWidth: '400px' }}>
            <Card elevation={3}>
              <CardContent sx={{ textAlign: 'center', p: 3 }}>
                <Avatar
                  src={petData.profilePhoto}
                  sx={{
                    width: 150,
                    height: 150,
                    mx: 'auto',
                    mb: 2,
                    border: '4px solid',
                    borderColor: 'primary.main'
                  }}
                >
                  <Pets sx={{ fontSize: 60 }} />
                </Avatar>
                <Typography variant="h5" gutterBottom>
                  {petData.name}
                </Typography>
                <Chip
                  label={content.profileComplete}
                  color="success"
                  variant="outlined"
                  sx={{ fontWeight: 'bold' }}
                />
              </CardContent>
            </Card>
          </Box>
        )}

        {/* Pet Details */}
        <Box sx={{ flex: '1 1 350px', maxWidth: '500px' }}>
          <Card elevation={3}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Pets color="primary" />
                {content.petDetails}
              </Typography>

              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                <Box sx={{ flex: '1 1 200px', minWidth: '150px' }}>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      {content.type}
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {formatType(petData.type)}
                    </Typography>
                  </Box>
                </Box>

                {petData.breed && (
                  <Box sx={{ flex: '1 1 200px', minWidth: '150px' }}>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        {content.breed}
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {petData.breed}
                      </Typography>
                    </Box>
                  </Box>
                )}

                {petData.age && (
                  <Box sx={{ flex: '1 1 200px', minWidth: '150px' }}>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        {content.age}
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {petData.age} {userLanguage === 'zh-CN' ? '岁' : userLanguage === 'es' ? 'años' : userLanguage === 'fr' ? 'ans' : 'years'}
                      </Typography>
                    </Box>
                  </Box>
                )}

                {petData.gender && (
                  <Box sx={{ flex: '1 1 200px', minWidth: '150px' }}>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        {content.sex}
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {formatGender(petData.gender)}
                      </Typography>
                    </Box>
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Next Steps */}
      <Box mb={4}>
        <Typography variant="h5" component="h3" gutterBottom>
          {content.nextSteps}
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 600, mx: 'auto', mb: 3 }}>
          {content.nextStepsDesc}
        </Typography>
      </Box>

      {/* Action Buttons */}
      <Box display="flex" justifyContent="center" gap={2} flexWrap="wrap">
        {onCancel && (
          <Button
            variant="outlined"
            size="large"
            startIcon={<ArrowBack />}
            onClick={onCancel}
            sx={{ minWidth: 180 }}
          >
            {content.backToPetSnap}
          </Button>
        )}

        <Button
          variant="contained"
          size="large"
          startIcon={<Visibility />}
          sx={{ minWidth: 180 }}
        >
          {content.viewProfile}
        </Button>

        {onAddAnotherPet && (
          <Button
            variant="outlined"
            size="large"
            startIcon={<Add />}
            onClick={onAddAnotherPet}
            sx={{ minWidth: 180 }}
          >
            {content.addAnotherPet}
          </Button>
        )}

        <Button
          variant="text"
          size="large"
          color="primary"
          sx={{ minWidth: 180 }}
        >
          {content.exploreFeatures}
        </Button>
      </Box>

      {/* AI Description Display */}
      {petData.aiAnalysisData?.description && (
        <Box mt={4}>
          <Card elevation={2} sx={{ bgcolor: 'primary.light', color: 'white', maxWidth: 600, mx: 'auto' }}>
            <CardContent>
              <Typography variant="body1" sx={{ fontStyle: 'italic', color: 'white', opacity: 0.9 }}>
                {petData.aiAnalysisData.description}
              </Typography>
            </CardContent>
          </Card>
        </Box>
      )}

      {/* Call to Action */}
      <Box mt={4}>
        <Typography variant="h6" color="primary">
          {content.startExploring}
        </Typography>
      </Box>
    </Box>
  );
};
